import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Search, X } from "lucide-react";
import api from "@/lib/api";

const GROUPS = [
  { key: "pages", label: "Pages", render: (item) => ({ to: item.path, title: item.label, meta: "Page" }) },
  { key: "plats", label: "À la carte", render: (item) => ({ to: "/a-emporter", title: item.name_fr, meta: item.price }) },
  { key: "produits", label: "Le Comptoir", render: (item) => ({ to: "/comptoir", title: item.name, meta: `${String(item.price.toFixed(2)).replace(".", ",")} €` }) },
  { key: "prestations", label: "Maasaï Services", render: (item) => ({ to: "/services", title: item.name, meta: item.price > 0 ? `${String(item.price.toFixed(2)).replace(".", ",")} €${item.unit ? ` ${item.unit}` : ""}` : "Sur devis" }) },
  { key: "evenements", label: "Événements", render: (item) => ({ to: "/evenements", title: item.title, meta: item.date }) },
];

export default function SearchOverlay({ open, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setResults(null);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults(null);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      api
        .get(`/search?q=${encodeURIComponent(query.trim())}`)
        .then((r) => setResults(r.data))
        .catch(() => setResults(null))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const hasResults =
    results && GROUPS.some((g) => (results[g.key] || []).length > 0);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] flex flex-col bg-ink/[0.985] backdrop-blur-2xl"
          data-testid="search-overlay"
        >
          <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-6 pt-24 md:pt-32">
            <div className="flex items-center gap-4 border-b border-white/15 pb-5">
              <Search className="h-6 w-6 shrink-0 text-gold" strokeWidth={1.5} />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher une page, un plat, un produit, une prestation…"
                className="w-full bg-transparent text-xl font-medium text-ivory outline-none placeholder:text-ivory/30 md:text-2xl"
                data-testid="search-input"
              />
              <button
                onClick={onClose}
                aria-label="Fermer la recherche"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold"
                data-testid="search-close-button"
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-8" data-testid="search-results">
              {query.trim().length < 2 ? (
                <p className="text-sm uppercase tracking-[0.25em] text-ivory/30" data-testid="search-hint">
                  Tapez au moins 2 caractères
                </p>
              ) : searching && !results ? (
                <p className="text-sm text-ivory/40" data-testid="search-loading">Recherche…</p>
              ) : results && !hasResults ? (
                <p className="text-sm text-ivory/40" data-testid="search-no-results">
                  Aucun résultat pour « {query} ».
                </p>
              ) : (
                results &&
                GROUPS.map((group) => {
                  const items = results[group.key] || [];
                  if (items.length === 0) return null;
                  return (
                    <div key={group.key} className="mb-8" data-testid={`search-group-${group.key}`}>
                      <p className="overline mb-4">{group.label}</p>
                      <div className="space-y-1">
                        {items.map((item, i) => {
                          const r = group.render(item);
                          return (
                            <Link
                              key={`${group.key}-${i}`}
                              to={r.to}
                              onClick={onClose}
                              className="group flex items-center gap-4 rounded-md px-3 py-3 transition-colors hover:bg-white/5"
                              data-testid={`search-result-${group.key}-${i}`}
                            >
                              <span className="min-w-0 flex-1 truncate text-base font-medium text-ivory/85 transition-colors group-hover:text-gold">
                                {r.title}
                              </span>
                              <span className="shrink-0 text-xs uppercase tracking-wider text-ivory/40">
                                {r.meta}
                              </span>
                              <ArrowRight className="h-4 w-4 shrink-0 text-gold opacity-0 transition-opacity group-hover:opacity-100" strokeWidth={1.5} />
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
